import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import Slider from "react-slick";
import Lottie from "lottie-react";
import { fetchCarListings } from "../../features/carListing/carListingSlice";
import Loader from "../Loader";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../../css/car_listing.css";
import { FaCheckCircle, FaChevronLeft, FaChevronRight, FaTimesCircle } from "react-icons/fa";
import { MdPending } from "react-icons/md";
import { Badge } from "react-bootstrap";
import { resolveListingUser } from "../../utils/carUserUtils";

const CarListingBookedLayer = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const searchTerm = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const value = params.get("search");
    return value ? value.trim() : "";
  }, [location.search]);

  const carListings = useSelector((state) => state.carListing?.items);
  const status = useSelector((state) => state.carListing?.status);
  const error = useSelector((state) => state.carListing?.error);
  const hasMore = useSelector((state) => state.carListing?.hasMore);
  const currentPage = useSelector((state) => state.carListing?.currentPage);
  const matchType = useSelector((state) => state.carListing?.matchType);
  const apiSearchQuery = useSelector((state) => state.carListing?.searchQuery);
  const apiCount = useSelector((state) => state.carListing?.count);

  const scrollStorageKey = useMemo(
    () => `car-listing-booked-${searchTerm || "all"}`,
    [searchTerm]
  );
  const scrollSavedRef = useRef(false);

  // Filter car listings to show only booked cars
  const filteredCarListings = useMemo(() => {
    let listings = carListings || [];

    // Filter for booked cars
    listings = listings.filter((car) => car.booked === true);

    if (!searchTerm) {
      return listings;
    }

    const loweredSearch = searchTerm.toLowerCase();

    return listings.filter((car) => {
      const userInfo = resolveListingUser(car || {});
      const candidates = [
        car.listingTitle,
        car.title,
        car.make,
        car.model,
        car.primaryDamage,
        userInfo.email,
        userInfo.name,
      ];

      return candidates.some(
        (value) =>
          typeof value === "string" &&
          value.toLowerCase().includes(loweredSearch)
      );
    });
  }, [carListings, searchTerm]);

  const [loadingCardId, setLoadingCardId] = useState(null);
  const [page, setPage] = useState(1);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [shouldRestoreScroll, setShouldRestoreScroll] = useState(true);
  const [noDataAnimation, setNoDataAnimation] = useState(null);

  useEffect(() => {
    setShouldRestoreScroll(true);
  }, [searchTerm]);

  const saveScrollPosition = useCallback(() => {
    if (typeof window === "undefined" || !("sessionStorage" in window)) return;
    try {
      sessionStorage.setItem(scrollStorageKey, String(window.scrollY));
      scrollSavedRef.current = true;
    } catch {
      // Ignore storage errors (e.g., disabled storage)
    }
  }, [scrollStorageKey]);

  useEffect(() => {
    return () => {
      if (scrollSavedRef.current) {
        scrollSavedRef.current = false;
        return;
      }
      saveScrollPosition();
    };
  }, [saveScrollPosition]);

  useEffect(() => {
    if (!shouldRestoreScroll) return;
    if (status === "loading") return;
    if (typeof window === "undefined" || !("sessionStorage" in window)) {
      setShouldRestoreScroll(false);
      return;
    }

    try {
      const storedScroll = sessionStorage.getItem(scrollStorageKey);
      if (storedScroll !== null) {
        const scrollValue = Number(storedScroll);
        if (!Number.isNaN(scrollValue)) {
          const performScroll = () => {
            window.scrollTo({ top: scrollValue });
          };
          window.requestAnimationFrame(() => {
            performScroll();
            window.requestAnimationFrame(performScroll);
            setTimeout(performScroll, 50);
          });
        }
      }
    } catch (error) {
      // Ignore storage errors
    } finally {
      setShouldRestoreScroll(false);
      scrollSavedRef.current = false;
    }
  }, [status, shouldRestoreScroll, scrollStorageKey]);

  // Load Lottie animation data
  useEffect(() => {
    const loadAnimation = async () => {
      try {
        let response = await fetch('/no-data-animation.json');
        if (!response.ok) {
          response = await fetch('https://assets5.lottiefiles.com/packages/lf20_qp1spzqv.json');
        }
        if (response.ok) {
          const data = await response.json();
          setNoDataAnimation(data);
        } else {
          throw new Error('Failed to load animation');
        }
      } catch (error) {
        setNoDataAnimation({
          v: "5.7.4",
          fr: 30,
          ip: 0,
          op: 90,
          w: 400,
          h: 400,
          nm: "Empty State",
          ddd: 0,
          assets: [],
          layers: [{
            ddd: 0,
            ind: 1,
            ty: 4,
            nm: "Empty Box",
            sr: 1,
            ks: {
              o: { a: 0, k: 100 },
              r: { a: 1, k: [
                { i: { x: [0.833], y: [0.833] }, o: { x: [0.167], y: [0.167] }, t: 0, s: [0] },
                { t: 90, s: [360] }
              ]},
              p: { a: 0, k: [200, 200, 0] },
              a: { a: 0, k: [0, 0, 0] },
              s: { a: 0, k: [100, 100, 100] }
            },
            ao: 0,
            shapes: [{
              ty: "gr",
              it: [{
                ty: "rc",
                d: 1,
                s: { a: 0, k: [120, 120] },
                p: { a: 0, k: [0, 0] },
                r: { a: 0, k: 12 },
                nm: "Rectangle Path 1",
                closed: true
              }, {
                ty: "st",
                c: { a: 0, k: [0.4, 0.4, 0.4, 1] },
                o: { a: 0, k: 100 },
                w: { a: 0, k: 3 },
                lc: 2,
                lj: 1,
                ml: 4,
                bm: 0,
                nm: "Stroke 1"
              }, {
                ty: "tr",
                p: { a: 0, k: [0, 0] },
                a: { a: 0, k: [0, 0] },
                s: { a: 0, k: [100, 100] },
                r: { a: 0, k: 0 },
                o: { a: 0, k: 100 },
                sk: { a: 0, k: 0 },
                sa: { a: 0, k: 0 },
                nm: "Transform"
              }],
              nm: "Rectangle 1",
              bm: 0
            }],
            ip: 0,
            op: 90,
            st: 0,
            bm: 0
          }]
        });
      }
    };
    
    loadAnimation();
  }, []);

  useEffect(() => {
    setIsFilterLoading(true);
    
    const fetchArgs = {
      page: 1,
      limit: 12,
      status: "all",
      ...(searchTerm ? { search: searchTerm } : {}),
    };

    dispatch(fetchCarListings(fetchArgs));
    setPage(1);
    setLoadingCardId(null);
  }, [dispatch, searchTerm]);

  useEffect(() => {
    if (status === "succeeded" || status === "failed") {
      setIsFilterLoading(false);
    }
  }, [status]);

  // Infinite scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      if (scrollTop + windowHeight >= documentHeight - 200) {
        if (hasMore && status !== "loading") {
          const nextPage = (currentPage || page) + 1;

          const fetchArgs = {
            page: nextPage,
            limit: 12,
            status: "all",
            ...(searchTerm ? { search: searchTerm } : {}),
          };
          
          dispatch(fetchCarListings(fetchArgs));
          setPage(nextPage);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [
    hasMore,
    status,
    currentPage,
    page,
    dispatch,
    searchTerm,
  ]);

  const getStatusBadge = (approvalStatus) => {
    switch (approvalStatus) {
      case 'approved':
        return <Badge bg="success" className="d-flex align-items-center gap-1"><FaCheckCircle size={12} /> Approved</Badge>;
      case 'rejected':
        return <Badge bg="danger" className="d-flex align-items-center gap-1"><FaTimesCircle size={12} /> Rejected</Badge>;
      default:
        return <Badge bg="warning" className="d-flex align-items-center gap-1"><MdPending size={12} /> Pending</Badge>;
    }
  };

  const NextArrow = ({ onClick }) => (
    <div className="custom-arrow next" onClick={onClick}>
      <FaChevronRight />
    </div>
  );

  const PrevArrow = ({ onClick }) => (
    <div className="custom-arrow prev" onClick={onClick}>
      <FaChevronLeft />
    </div>
  );

  const carouselSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    autoplay: true,
    autoplaySpeed: 3000,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    pauseOnHover: true,
    fade: true
  };

  if (status === "loading" && (!carListings || carListings.length === 0)) return <Loader />;
  if (
    isFilterLoading &&
    (!filteredCarListings || filteredCarListings.length === 0)
  )
    return <Loader />;
  if (status === "failed" && (!carListings || carListings.length === 0)) return <p>{error}</p>;

  return (
      <div className="car-listing-container">
      {searchTerm && (
        <div className="alert alert-info d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 mb-4">
          <div>
            {matchType === "pattern"
              ? `Showing related results for "${apiSearchQuery || searchTerm}"`
              : `Showing results for "${apiSearchQuery || searchTerm}"`}
            {matchType === "pattern" && (
              <span className="text-muted ms-1">(broader match)</span>
            )}
          </div>
          <div className="text-muted text-sm">
            {new Intl.NumberFormat().format(apiCount || filteredCarListings.length)} result
            {Number(apiCount || filteredCarListings.length) === 1 ? "" : "s"}
          </div>
        </div>
      )}
      {filteredCarListings && filteredCarListings.length === 0 && carListings && carListings.length > 0 && (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            {noDataAnimation && (
              <Lottie
                animationData={noDataAnimation}
                loop={true}
                autoplay={true}
                style={{ width: 300, height: 300, margin: '0 auto 20px' }}
                rendererSettings={{
                  preserveAspectRatio: 'xMidYMid slice'
                }}
              />
            )}
            <p className="text-muted" style={{ fontSize: '18px', marginTop: '16px' }}>
              No booked cars found.
            </p>
          </div>
        </div>
      )}
      {status === 'succeeded' && filteredCarListings && filteredCarListings.length === 0 && (!carListings || carListings.length === 0) && (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            {noDataAnimation && (
              <Lottie
                animationData={noDataAnimation}
                loop={true}
                autoplay={true}
                style={{ width: 300, height: 300, margin: '0 auto 20px' }}
                rendererSettings={{
                  preserveAspectRatio: 'xMidYMid slice'
                }}
              />
            )}
            <p className="text-muted" style={{ fontSize: '18px', marginTop: '16px' }}>
              No booked cars found.
            </p>
          </div>
        </div>
      )}
      <div className="row gy-4">
        {filteredCarListings && filteredCarListings.length > 0 && filteredCarListings.map((a, idx) => {
          const userInfo = resolveListingUser(a || {});
          const displayAvatar = userInfo.avatar || a?.userId?.avatar;
          const displayEmail = userInfo.email || a?.userId?.email;
          
          // Get buyer email from various possible fields (including MongoDB structure)
          const buyerEmail = 
            a?.BookedBy ||  // Capital B (direct string from MongoDB)
            a?.buyer ||    // Lowercase (direct string from MongoDB)
            a?.buyerEmail || 
            (typeof a?.buyer === 'object' && a?.buyer?.email) ||
            a?.bookedBy?.email || 
            a?.submission?.bookedBy?.email ||
            a?.buyerId?.email || 
            (typeof a?.buyerId === 'object' && a?.buyerId?.email) ||
            a?.purchasedBy?.email ||
            null;
          
          // Get payment method
          const paymentMethod = a?.paymentMethod || a?.submission?.paymentMethod || null;
          
          // Get booked date
          const bookedAtDate = a?.bookedAtDate || a?.submission?.bookedAtDate || null;
          const formattedBookedDate = bookedAtDate 
            ? new Date(bookedAtDate).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })
            : null;

          return (
            <div className="col-xxl-3 col-lg-4 col-sm-6" key={a._id || idx}>
            <div 
              className="car-listing-card"
              style={{ cursor: 'pointer' }}
            >
              {/* Loading Overlay */}
              {loadingCardId === a._id && (
                <div className="card-loading-overlay">
                  <div className="card-loading-spinner"></div>
                  <span className="card-loading-text">Loading...</span>
                </div>
              )}
              {/* Image Carousel */}
              <div className="car-image-container">
                {a.images && a.images.length > 0 && (
                  <Slider {...carouselSettings}>
                    {a.images.map((img, i) => (
                      <div key={i}>
                        <img
                          src={img.url}
                          alt={`Slide ${i + 1}`}
                          className="car-image"
                        />
                      </div>
                    ))}
                  </Slider>
                )}
                {a.img && a.img.length > 0 && (
                  <Slider {...carouselSettings}>
                    {a.img.map((img, i) => (
                      <div key={i}>
                        <img
                          src={img.url || img.src}
                          alt={`Slide ${i + 1}`}
                          className="car-image"
                        />
                      </div>
                    ))}
                  </Slider>
                )}
                
                {/* Status Badge Overlay */}
                <div className="status-badge-overlay">
                  {getStatusBadge(a.approvalStatus)}
                  {a.booked && (
                    <Badge bg="info" className="d-flex align-items-center gap-1 ms-2">
                      <i className="ri-book-line"></i> Booked
                    </Badge>
                  )}
                </div>
              </div>

              {/* Card Content */}
              <div className="car-card-content">
                {/* Header */}
                <div className="car-header d-flex flex-row justify-content-between align-items-center">
                  <h6 className="car-title text-capitalize text-start text-xs">{a.listingTitle || a.title}</h6>
                  <div className="price-tag">
                    <span>{Number(a.askingPrice || a.price).toLocaleString()} AED</span>
                  </div>
                </div>

                {/* Seller/Dealer Info */}
                <div className="user-info-section">
                  <div className="user-avatar">
                    <img 
                      src={displayAvatar || "/default-avatar.png"}
                      alt="User Avatar"
                      className="avatar-image"
                    />
                  </div>
                  <div className="user-details">
                    <p className="user-label">Seller/Dealer</p>
                    <p className="user-email">{displayEmail || "No Email"}</p>
                  </div>
                </div>

                {/* Buyer Info */}
                {buyerEmail && (
                  <div className="buyer-info-section">
                    <div className="buyer-icon">
                      <i className="ri-user-line"></i>
                    </div>
                    <div className="buyer-details">
                      <p className="buyer-label">Buyer</p>
                      <p className="buyer-email">{buyerEmail}</p>
                      {paymentMethod && (
                        <p className="buyer-payment">
                          <i className="ri-bank-card-line me-1"></i>
                          Payment: <strong>{paymentMethod.toUpperCase()}</strong>
                        </p>
                      )}
                      {formattedBookedDate && (
                        <p className="buyer-date">
                          <i className="ri-calendar-line me-1"></i>
                          Booked: <strong>{formattedBookedDate}</strong>
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Car Details */}
                <div className="car-details">
                  <div className="detail-row">
                    <span className="detail-label">Year:</span>
                    <span className="detail-value">{a.year}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Make:</span>
                    <span className="detail-value">{a.make}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Condition:</span>
                    <span className="detail-value">{a.condition}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Mileage:</span>
                    <span className="detail-value">{(a.mileage).toLocaleString()}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Primary Damage:</span>
                    <span className="detail-value">{a.primaryDamage}</span>
                  </div>
                  {a?.repairingCost && <div className="detail-row">
                    <span className="detail-label">Repairing Cost:</span>
                    <span className="detail-value">{a?.repairingCost || 'N/A'} AED</span>
                  </div>}
                </div>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {status === 'loading' && filteredCarListings && filteredCarListings.length > 0 && (
        <div className="d-flex justify-content-center my-4">
          <div className="loading-indicator">
            <div className="spinner"></div>
            <span>Loading more cars...</span>
          </div>
        </div>
      )}

      <style jsx="true">{`
        .car-listing-container {
          padding: 20px 0;
        }

        .car-listing-card {
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          overflow: hidden;
          transition: all 0.3s ease;
          height: 100%;
          border: 1px solid #f0f0f0;
          position: relative;
        }

        .car-listing-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        }

        .card-loading-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.95);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 10;
          border-radius: 16px;
          backdrop-filter: blur(2px);
        }

        .card-loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 12px;
        }

        .card-loading-text {
          font-size: 14px;
          font-weight: 600;
          color: #667eea;
        }

        .car-image-container {
          position: relative;
          height: 220px;
          overflow: hidden;
        }

        .car-image {
          width: 100%;
          height: 220px;
          object-fit: cover;
        }

        .status-badge-overlay {
          position: absolute;
          top: 12px;
          right: 12px;
          z-index: 1;
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
        }

        .car-card-content {
          padding: 20px;
        }

        .car-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }

        .car-title {
          font-size: 16px;
          font-weight: 600;
          color: #2c3e50;
          margin: 0;
          flex: 1;
          line-height: 1.3;
        }

        .price-tag {
          display: flex;
          align-items: center;
          gap: 4px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 6px 12px;
          border-radius: 20px;
          font-weight: 600;
          font-size: 14px;
          margin-left: 12px;
        }

        .user-info-section {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 0;
          border-bottom: 1px solid #f0f0f0;
          margin-bottom: 16px;
        }

        .user-avatar {
          flex-shrink: 0;
        }

        .avatar-image {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          object-fit: cover;
          border: 2px solid #e9ecef;
        }

        .user-details {
          flex: 1;
        }

        .user-label {
          margin: 0 0 4px 0;
          font-size: 11px;
          color: #6c757d;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .user-email {
          margin: 0;
          font-size: 14px;
          color: #6c757d;
          font-weight: 500;
        }

        .buyer-info-section {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: #f8f9ff;
          border-radius: 8px;
          border: 1px solid #e0e7ff;
          margin-bottom: 16px;
        }

        .buyer-icon {
          flex-shrink: 0;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 50%;
          color: white;
          font-size: 18px;
        }

        .buyer-details {
          flex: 1;
        }

        .buyer-label {
          margin: 0 0 4px 0;
          font-size: 11px;
          color: #667eea;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .buyer-email {
          margin: 0 0 6px 0;
          font-size: 14px;
          color: #2c3e50;
          font-weight: 600;
        }

        .buyer-payment,
        .buyer-date {
          margin: 4px 0 0 0;
          font-size: 12px;
          color: #495057;
          font-weight: 500;
        }

        .buyer-payment strong,
        .buyer-date strong {
          color: #667eea;
          font-weight: 600;
        }

        .car-details {
          margin-bottom: 16px;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 6px 0;
          border-bottom: 1px solid #f8f9fa;
        }

        .detail-label {
          font-weight: 600;
          color: #495057;
          font-size: 14px;
        }

        .detail-value {
          color: #6c757d;
          font-size: 14px;
        }

        .loading-indicator {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          color: #6c757d;
          font-size: 14px;
        }

        .loading-indicator .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid #f3f3f3;
          border-top: 2px solid #007bff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .custom-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 1;
          background: rgba(0, 0, 0, 0.6);
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          cursor: pointer;
          color: #fff;
          transition: all 0.2s ease;
        }

        .custom-arrow:hover {
          background: rgba(0, 0, 0, 0.8);
          transform: translateY(-50%) scale(1.1);
        }

        .custom-arrow.next {
          right: 12px;
        }

        .custom-arrow.prev {
          left: 12px;
        }

        .slick-dots {
          bottom: 20px;
          display: flex !important;
          justify-content: center;
          padding: 0;
          margin: 0;
          list-style: none;
          z-index: 2;
        }

        .slick-dots li {
          width: 12px;
          height: 12px;
          margin: 0 4px;
        }

        .slick-dots li button {
          width: 12px;
          height: 12px;
          padding: 0;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.5);
          border: 2px solid rgba(255, 255, 255, 0.8);
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .slick-dots li button:hover {
          background: rgba(255, 255, 255, 0.8);
          transform: scale(1.1);
        }

        .slick-dots li.slick-active button {
          background: #fff;
          border-color: #fff;
          transform: scale(1.3);
          box-shadow: 0 0 8px rgba(255, 255, 255, 0.6);
        }

        .slick-dots li button:before {
          content: none;
        }

        @media (max-width: 768px) {
          .car-listing-card {
            margin-bottom: 20px;
          }

          .car-header {
            flex-direction: column;
            gap: 8px;
          }

          .price-tag {
            align-self: flex-start;
            margin-left: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default CarListingBookedLayer;

// import { useEffect, useState, useMemo, useRef } from "react";
// import { useSelector, useDispatch } from "react-redux";
// import { useLocation, useNavigate } from "react-router-dom";
// import Slider from "react-slick";
// import Lottie from "lottie-react";
// import { fetchCarListings } from "../../features/carListing/carListingSlice";
// import Loader from "../Loader";
// import "slick-carousel/slick/slick.css";
// import "slick-carousel/slick/slick-theme.css";
// import "../../css/car_listing.css";
// import { FaCheckCircle, FaChevronLeft, FaChevronRight, FaTimesCircle } from "react-icons/fa";
// import { MdPending } from "react-icons/md";
// import { Badge } from "react-bootstrap";
// import { resolveListingUser } from "../../utils/carUserUtils";

// const CarListingBookedLayer = () => {
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const location = useLocation();

//   const searchTerm = useMemo(() => {
//     const params = new URLSearchParams(location.search);
//     const value = params.get("search");
//     return value ? value.trim() : "";
//   }, [location.search]);

//   const carListings = useSelector((state) => state.carListing?.items);
//   const status = useSelector((state) => state.carListing?.status);
//   const error = useSelector((state) => state.carListing?.error);
//   const hasMore = useSelector((state) => state.carListing?.hasMore);
//   const currentPage = useSelector((state) => state.carListing?.currentPage);
//   const matchType = useSelector((state) => state.carListing?.matchType);
//   const apiSearchQuery = useSelector((state) => state.carListing?.searchQuery);
//   const apiCount = useSelector((state) => state.carListing?.count);

//   const scrollStorageKey = useMemo(
//     () => `car-listing-booked-${searchTerm || "all"}`,
//     [searchTerm]
//   );
//   const scrollSavedRef = useRef(false);

//   // Filter car listings to show only booked cars
//   const filteredCarListings = useMemo(() => {
//     let listings = carListings || [];

//     // Filter for booked cars
//     listings = listings.filter((car) => car.booked === true);

//     if (!searchTerm) {
//       return listings;
//     }

//     const loweredSearch = searchTerm.toLowerCase();

//     return listings.filter((car) => {
//       const userInfo = resolveListingUser(car || {});
//       const candidates = [
//         car.listingTitle,
//         car.title,
//         car.make,
//         car.model,
//         car.primaryDamage,
//         userInfo.email,
//         userInfo.name,
//       ];

//       return candidates.some(
//         (value) =>
//           typeof value === "string" &&
//           value.toLowerCase().includes(loweredSearch)
//       );
//     });
//   }, [carListings, searchTerm]);

//   const [loadingCardId, setLoadingCardId] = useState(null);
//   const [page, setPage] = useState(1);
//   const [isFilterLoading, setIsFilterLoading] = useState(false);
//   const [shouldRestoreScroll, setShouldRestoreScroll] = useState(true);
//   const [noDataAnimation, setNoDataAnimation] = useState(null);

//   useEffect(() => {
//     setShouldRestoreScroll(true);
//   }, [searchTerm]);

//   const saveScrollPosition = () => {
//     if (typeof window === "undefined" || !("sessionStorage" in window)) return;
//     try {
//       sessionStorage.setItem(scrollStorageKey, String(window.scrollY));
//       scrollSavedRef.current = true;
//     } catch {
//       // Ignore storage errors (e.g., disabled storage)
//     }
//   };

//   useEffect(() => {
//     return () => {
//       if (scrollSavedRef.current) {
//         scrollSavedRef.current = false;
//         return;
//       }
//       saveScrollPosition();
//     };
//   }, [scrollStorageKey]);

//   useEffect(() => {
//     if (!shouldRestoreScroll) return;
//     if (status === "loading") return;
//     if (typeof window === "undefined" || !("sessionStorage" in window)) {
//       setShouldRestoreScroll(false);
//       return;
//     }

//     try {
//       const storedScroll = sessionStorage.getItem(scrollStorageKey);
//       if (storedScroll !== null) {
//         const scrollValue = Number(storedScroll);
//         if (!Number.isNaN(scrollValue)) {
//           const performScroll = () => {
//             window.scrollTo({ top: scrollValue });
//           };
//           window.requestAnimationFrame(() => {
//             performScroll();
//             window.requestAnimationFrame(performScroll);
//             setTimeout(performScroll, 50);
//           });
//         }
//       }
//     } catch (error) {
//       // Ignore storage errors
//     } finally {
//       setShouldRestoreScroll(false);
//       scrollSavedRef.current = false;
//     }
//   }, [status, shouldRestoreScroll, scrollStorageKey]);

//   // Load Lottie animation data
//   useEffect(() => {
//     const loadAnimation = async () => {
//       try {
//         let response = await fetch('/no-data-animation.json');
//         if (!response.ok) {
//           response = await fetch('https://assets5.lottiefiles.com/packages/lf20_qp1spzqv.json');
//         }
//         if (response.ok) {
//           const data = await response.json();
//           setNoDataAnimation(data);
//         } else {
//           throw new Error('Failed to load animation');
//         }
//       } catch (error) {
//         setNoDataAnimation({
//           v: "5.7.4",
//           fr: 30,
//           ip: 0,
//           op: 90,
//           w: 400,
//           h: 400,
//           nm: "Empty State",
//           ddd: 0,
//           assets: [],
//           layers: [{
//             ddd: 0,
//             ind: 1,
//             ty: 4,
//             nm: "Empty Box",
//             sr: 1,
//             ks: {
//               o: { a: 0, k: 100 },
//               r: { a: 1, k: [
//                 { i: { x: [0.833], y: [0.833] }, o: { x: [0.167], y: [0.167] }, t: 0, s: [0] },
//                 { t: 90, s: [360] }
//               ]},
//               p: { a: 0, k: [200, 200, 0] },
//               a: { a: 0, k: [0, 0, 0] },
//               s: { a: 0, k: [100, 100, 100] }
//             },
//             ao: 0,
//             shapes: [{
//               ty: "gr",
//               it: [{
//                 ty: "rc",
//                 d: 1,
//                 s: { a: 0, k: [120, 120] },
//                 p: { a: 0, k: [0, 0] },
//                 r: { a: 0, k: 12 },
//                 nm: "Rectangle Path 1",
//                 closed: true
//               }, {
//                 ty: "st",
//                 c: { a: 0, k: [0.4, 0.4, 0.4, 1] },
//                 o: { a: 0, k: 100 },
//                 w: { a: 0, k: 3 },
//                 lc: 2,
//                 lj: 1,
//                 ml: 4,
//                 bm: 0,
//                 nm: "Stroke 1"
//               }, {
//                 ty: "tr",
//                 p: { a: 0, k: [0, 0] },
//                 a: { a: 0, k: [0, 0] },
//                 s: { a: 0, k: [100, 100] },
//                 r: { a: 0, k: 0 },
//                 o: { a: 0, k: 100 },
//                 sk: { a: 0, k: 0 },
//                 sa: { a: 0, k: 0 },
//                 nm: "Transform"
//               }],
//               nm: "Rectangle 1",
//               bm: 0
//             }],
//             ip: 0,
//             op: 90,
//             st: 0,
//             bm: 0
//           }]
//         });
//       }
//     };
    
//     loadAnimation();
//   }, []);

//   useEffect(() => {
//     setIsFilterLoading(true);
    
//     const fetchArgs = {
//       page: 1,
//       limit: 12,
//       status: "all",
//       ...(searchTerm ? { search: searchTerm } : {}),
//     };

//     dispatch(fetchCarListings(fetchArgs));
//     setPage(1);
//     setLoadingCardId(null);
//   }, [dispatch, searchTerm]);

//   useEffect(() => {
//     if (status === "succeeded" || status === "failed") {
//       setIsFilterLoading(false);
//     }
//   }, [status]);

//   // Infinite scroll effect
//   useEffect(() => {
//     const handleScroll = () => {
//       const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
//       const windowHeight = window.innerHeight;
//       const documentHeight = document.documentElement.scrollHeight;

//       if (scrollTop + windowHeight >= documentHeight - 200) {
//         if (hasMore && status !== "loading") {
//           const nextPage = (currentPage || page) + 1;

//           const fetchArgs = {
//             page: nextPage,
//             limit: 12,
//             status: "all",
//             ...(searchTerm ? { search: searchTerm } : {}),
//           };
          
//           dispatch(fetchCarListings(fetchArgs));
//           setPage(nextPage);
//         }
//       }
//     };

//     window.addEventListener('scroll', handleScroll);

//     return () => {
//       window.removeEventListener('scroll', handleScroll);
//     };
//   }, [
//     hasMore,
//     status,
//     currentPage,
//     page,
//     dispatch,
//     searchTerm,
//   ]);

//   const handleCardClick = (listingId) => {
//     saveScrollPosition();
//     setLoadingCardId(listingId);
//     navigate(`/car-listing-detail/${listingId}`, {
//       state: {
//         fromListPath: location.pathname,
//         approvalStatusFilter: "booked",
//       },
//     });
//   };

//   const getStatusBadge = (approvalStatus) => {
//     switch (approvalStatus) {
//       case 'approved':
//         return <Badge bg="success" className="d-flex align-items-center gap-1"><FaCheckCircle size={12} /> Approved</Badge>;
//       case 'rejected':
//         return <Badge bg="danger" className="d-flex align-items-center gap-1"><FaTimesCircle size={12} /> Rejected</Badge>;
//       default:
//         return <Badge bg="warning" className="d-flex align-items-center gap-1"><MdPending size={12} /> Pending</Badge>;
//     }
//   };

//   const NextArrow = ({ onClick }) => (
//     <div className="custom-arrow next" onClick={onClick}>
//       <FaChevronRight />
//     </div>
//   );

//   const PrevArrow = ({ onClick }) => (
//     <div className="custom-arrow prev" onClick={onClick}>
//       <FaChevronLeft />
//     </div>
//   );

//   const carouselSettings = {
//     dots: true,
//     infinite: true,
//     speed: 500,
//     autoplay: true,
//     autoplaySpeed: 3000,
//     slidesToShow: 1,
//     slidesToScroll: 1,
//     arrows: true,
//     nextArrow: <NextArrow />,
//     prevArrow: <PrevArrow />,
//     pauseOnHover: true,
//     fade: true
//   };

//   if (status === "loading" && (!carListings || carListings.length === 0)) return <Loader />;
//   if (
//     isFilterLoading &&
//     (!filteredCarListings || filteredCarListings.length === 0)
//   )
//     return <Loader />;
//   if (status === "failed" && (!carListings || carListings.length === 0)) return <p>{error}</p>;

//   return (
//       <div className="car-listing-container">
//       {searchTerm && (
//         <div className="alert alert-info d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 mb-4">
//           <div>
//             {matchType === "pattern"
//               ? `Showing related results for "${apiSearchQuery || searchTerm}"`
//               : `Showing results for "${apiSearchQuery || searchTerm}"`}
//             {matchType === "pattern" && (
//               <span className="text-muted ms-1">(broader match)</span>
//             )}
//           </div>
//           <div className="text-muted text-sm">
//             {new Intl.NumberFormat().format(apiCount || filteredCarListings.length)} result
//             {Number(apiCount || filteredCarListings.length) === 1 ? "" : "s"}
//           </div>
//         </div>
//       )}
//       {filteredCarListings && filteredCarListings.length === 0 && carListings && carListings.length > 0 && (
//         <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
//           <div className="text-center">
//             {noDataAnimation && (
//               <Lottie
//                 animationData={noDataAnimation}
//                 loop={true}
//                 autoplay={true}
//                 style={{ width: 300, height: 300, margin: '0 auto 20px' }}
//                 rendererSettings={{
//                   preserveAspectRatio: 'xMidYMid slice'
//                 }}
//               />
//             )}
//             <p className="text-muted" style={{ fontSize: '18px', marginTop: '16px' }}>
//               No booked cars found.
//             </p>
//           </div>
//         </div>
//       )}
//       {status === 'succeeded' && filteredCarListings && filteredCarListings.length === 0 && (!carListings || carListings.length === 0) && (
//         <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
//           <div className="text-center">
//             {noDataAnimation && (
//               <Lottie
//                 animationData={noDataAnimation}
//                 loop={true}
//                 autoplay={true}
//                 style={{ width: 300, height: 300, margin: '0 auto 20px' }}
//                 rendererSettings={{
//                   preserveAspectRatio: 'xMidYMid slice'
//                 }}
//               />
//             )}
//             <p className="text-muted" style={{ fontSize: '18px', marginTop: '16px' }}>
//               No booked cars found.
//             </p>
//           </div>
//         </div>
//       )}
//       <div className="row gy-4">
//         {filteredCarListings && filteredCarListings.length > 0 && filteredCarListings.map((a, idx) => {
//           const userInfo = resolveListingUser(a || {});
//           const displayAvatar = userInfo.avatar || a?.userId?.avatar;
//           const displayEmail = userInfo.email || a?.userId?.email;
          
//           // Get buyer email from various possible fields (including MongoDB structure)
//           const buyerEmail = 
//             a?.BookedBy ||  // Capital B (direct string from MongoDB)
//             a?.buyer ||    // Lowercase (direct string from MongoDB)
//             a?.buyerEmail || 
//             (typeof a?.buyer === 'object' && a?.buyer?.email) ||
//             a?.bookedBy?.email || 
//             a?.submission?.bookedBy?.email ||
//             a?.buyerId?.email || 
//             (typeof a?.buyerId === 'object' && a?.buyerId?.email) ||
//             a?.purchasedBy?.email ||
//             null;
          
//           // Get payment method
//           const paymentMethod = a?.paymentMethod || a?.submission?.paymentMethod || null;
          
//           // Get booked date
//           const bookedAtDate = a?.bookedAtDate || a?.submission?.bookedAtDate || null;
//           const formattedBookedDate = bookedAtDate 
//             ? new Date(bookedAtDate).toLocaleDateString('en-US', { 
//                 year: 'numeric', 
//                 month: 'short', 
//                 day: 'numeric',
//                 hour: '2-digit',
//                 minute: '2-digit'
//               })
//             : null;

//           return (
//             <div className="col-xxl-3 col-lg-4 col-sm-6" key={a._id || idx}>
//             <div 
//               className="car-listing-card"
//             //   onClick={() => handleCardClick(a._id)}
//               style={{ cursor: 'pointer' }}
//             >
//               {/* Loading Overlay */}
//               {loadingCardId === a._id && (
//                 <div className="card-loading-overlay">
//                   <div className="card-loading-spinner"></div>
//                   <span className="card-loading-text">Loading...</span>
//                 </div>
//               )}
//               {/* Image Carousel */}
//               <div className="car-image-container">
//                 {a.images && a.images.length > 0 && (
//                   <Slider {...carouselSettings}>
//                     {a.images.map((img, i) => (
//                       <div key={i}>
//                         <img
//                           src={img.url}
//                           alt={`Slide ${i + 1}`}
//                           className="car-image"
//                         />
//                       </div>
//                     ))}
//                   </Slider>
//                 )}
//                 {a.img && a.img.length > 0 && (
//                   <Slider {...carouselSettings}>
//                     {a.img.map((img, i) => (
//                       <div key={i}>
//                         <img
//                           src={img.url || img.src}
//                           alt={`Slide ${i + 1}`}
//                           className="car-image"
//                         />
//                       </div>
//                     ))}
//                   </Slider>
//                 )}
                
//                 {/* Status Badge Overlay */}
//                 <div className="status-badge-overlay">
//                   {getStatusBadge(a.approvalStatus)}
//                   {a.booked && (
//                     <Badge bg="info" className="d-flex align-items-center gap-1 ms-2">
//                       <i className="ri-book-line"></i> Booked
//                     </Badge>
//                   )}
//                 </div>
//               </div>

//               {/* Card Content */}
//               <div className="car-card-content">
//                 {/* Header */}
//                 <div className="car-header d-flex flex-row justify-content-between align-items-center">
//                   <h6 className="car-title text-capitalize text-start text-xs">{a.listingTitle || a.title}</h6>
//                   <div className="price-tag">
//                     <span>{Number(a.askingPrice || a.price).toLocaleString()} AED</span>
//                   </div>
//                 </div>

//                 {/* Seller/Dealer Info */}
//                 <div className="user-info-section">
//                   <div className="user-avatar">
//                     <img 
//                       src={displayAvatar || "/default-avatar.png"}
//                       alt="User Avatar"
//                       className="avatar-image"
//                     />
//                   </div>
//                   <div className="user-details">
//                     <p className="user-label">Seller/Dealer</p>
//                     <p className="user-email">{displayEmail || "No Email"}</p>
//                   </div>
//                 </div>

//                 {/* Buyer Info */}
//                 {buyerEmail && (
//                   <div className="buyer-info-section">
//                     <div className="buyer-icon">
//                       <i className="ri-user-line"></i>
//                     </div>
//                     <div className="buyer-details">
//                       <p className="buyer-label">Buyer</p>
//                       <p className="buyer-email">{buyerEmail}</p>
//                       {paymentMethod && (
//                         <p className="buyer-payment">
//                           <i className="ri-bank-card-line me-1"></i>
//                           Payment: <strong>{paymentMethod.toUpperCase()}</strong>
//                         </p>
//                       )}
//                       {formattedBookedDate && (
//                         <p className="buyer-date">
//                           <i className="ri-calendar-line me-1"></i>
//                           Booked: <strong>{formattedBookedDate}</strong>
//                         </p>
//                       )}
//                     </div>
//                   </div>
//                 )}

//                 {/* Car Details */}
//                 <div className="car-details">
//                   <div className="detail-row">
//                     <span className="detail-label">Year:</span>
//                     <span className="detail-value">{a.year}</span>
//                   </div>
//                   <div className="detail-row">
//                     <span className="detail-label">Make:</span>
//                     <span className="detail-value">{a.make}</span>
//                   </div>
//                   <div className="detail-row">
//                     <span className="detail-label">Condition:</span>
//                     <span className="detail-value">{a.condition}</span>
//                   </div>
//                   <div className="detail-row">
//                     <span className="detail-label">Mileage:</span>
//                     <span className="detail-value">{(a.mileage).toLocaleString()}</span>
//                   </div>
//                   <div className="detail-row">
//                     <span className="detail-label">Primary Damage:</span>
//                     <span className="detail-value">{a.primaryDamage}</span>
//                   </div>
//                   {a?.repairingCost && <div className="detail-row">
//                     <span className="detail-label">Repairing Cost:</span>
//                     <span className="detail-value">{a?.repairingCost || 'N/A'} AED</span>
//                   </div>}
//                 </div>
//               </div>
//             </div>
//           </div>
//           );
//         })}
//       </div>

//       {status === 'loading' && filteredCarListings && filteredCarListings.length > 0 && (
//         <div className="d-flex justify-content-center my-4">
//           <div className="loading-indicator">
//             <div className="spinner"></div>
//             <span>Loading more cars...</span>
//           </div>
//         </div>
//       )}

//       <style jsx="true">{`
//         .car-listing-container {
//           padding: 20px 0;
//         }

//         .car-listing-card {
//           background: #fff;
//           border-radius: 16px;
//           box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
//           overflow: hidden;
//           transition: all 0.3s ease;
//           height: 100%;
//           border: 1px solid #f0f0f0;
//           position: relative;
//         }

//         .car-listing-card:hover {
//           transform: translateY(-4px);
//           box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
//         }

//         .card-loading-overlay {
//           position: absolute;
//           top: 0;
//           left: 0;
//           right: 0;
//           bottom: 0;
//           background: rgba(255, 255, 255, 0.95);
//           display: flex;
//           flex-direction: column;
//           align-items: center;
//           justify-content: center;
//           z-index: 10;
//           border-radius: 16px;
//           backdrop-filter: blur(2px);
//         }

//         .card-loading-spinner {
//           width: 40px;
//           height: 40px;
//           border: 4px solid #f3f3f3;
//           border-top: 4px solid #667eea;
//           border-radius: 50%;
//           animation: spin 1s linear infinite;
//           margin-bottom: 12px;
//         }

//         .card-loading-text {
//           font-size: 14px;
//           font-weight: 600;
//           color: #667eea;
//         }

//         .car-image-container {
//           position: relative;
//           height: 220px;
//           overflow: hidden;
//         }

//         .car-image {
//           width: 100%;
//           height: 220px;
//           object-fit: cover;
//         }

//         .status-badge-overlay {
//           position: absolute;
//           top: 12px;
//           right: 12px;
//           z-index: 1;
//           display: flex;
//           flex-wrap: wrap;
//           gap: 4px;
//         }

//         .car-card-content {
//           padding: 20px;
//         }

//         .car-header {
//           display: flex;
//           justify-content: space-between;
//           align-items: flex-start;
//           margin-bottom: 16px;
//         }

//         .car-title {
//           font-size: 16px;
//           font-weight: 600;
//           color: #2c3e50;
//           margin: 0;
//           flex: 1;
//           line-height: 1.3;
//         }

//         .price-tag {
//           display: flex;
//           align-items: center;
//           gap: 4px;
//           background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
//           color: white;
//           padding: 6px 12px;
//           border-radius: 20px;
//           font-weight: 600;
//           font-size: 14px;
//           margin-left: 12px;
//         }

//         .user-info-section {
//           display: flex;
//           align-items: center;
//           gap: 12px;
//           padding: 12px 0;
//           border-bottom: 1px solid #f0f0f0;
//           margin-bottom: 16px;
//         }

//         .user-avatar {
//           flex-shrink: 0;
//         }

//         .avatar-image {
//           width: 40px;
//           height: 40px;
//           border-radius: 50%;
//           object-fit: cover;
//           border: 2px solid #e9ecef;
//         }

//         .user-details {
//           flex: 1;
//         }

//         .user-label {
//           margin: 0 0 4px 0;
//           font-size: 11px;
//           color: #6c757d;
//           font-weight: 600;
//           text-transform: uppercase;
//           letter-spacing: 0.5px;
//         }

//         .user-email {
//           margin: 0;
//           font-size: 14px;
//           color: #6c757d;
//           font-weight: 500;
//         }

//         .buyer-info-section {
//           display: flex;
//           align-items: center;
//           gap: 12px;
//           padding: 12px;
//           background: #f8f9ff;
//           border-radius: 8px;
//           border: 1px solid #e0e7ff;
//           margin-bottom: 16px;
//         }

//         .buyer-icon {
//           flex-shrink: 0;
//           width: 40px;
//           height: 40px;
//           display: flex;
//           align-items: center;
//           justify-content: center;
//           background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
//           border-radius: 50%;
//           color: white;
//           font-size: 18px;
//         }

//         .buyer-details {
//           flex: 1;
//         }

//         .buyer-label {
//           margin: 0 0 4px 0;
//           font-size: 11px;
//           color: #667eea;
//           font-weight: 600;
//           text-transform: uppercase;
//           letter-spacing: 0.5px;
//         }

//         .buyer-email {
//           margin: 0 0 6px 0;
//           font-size: 14px;
//           color: #2c3e50;
//           font-weight: 600;
//         }

//         .buyer-payment,
//         .buyer-date {
//           margin: 4px 0 0 0;
//           font-size: 12px;
//           color: #495057;
//           font-weight: 500;
//         }

//         .buyer-payment strong,
//         .buyer-date strong {
//           color: #667eea;
//           font-weight: 600;
//         }

//         .car-details {
//           margin-bottom: 16px;
//         }

//         .detail-row {
//           display: flex;
//           justify-content: space-between;
//           align-items: center;
//           padding: 6px 0;
//           border-bottom: 1px solid #f8f9fa;
//         }

//         .detail-label {
//           font-weight: 600;
//           color: #495057;
//           font-size: 14px;
//         }

//         .detail-value {
//           color: #6c757d;
//           font-size: 14px;
//         }

//         .loading-indicator {
//           display: flex;
//           align-items: center;
//           gap: 12px;
//           padding: 16px;
//           color: #6c757d;
//           font-size: 14px;
//         }

//         .loading-indicator .spinner {
//           width: 20px;
//           height: 20px;
//           border: 2px solid #f3f3f3;
//           border-top: 2px solid #007bff;
//           border-radius: 50%;
//           animation: spin 1s linear infinite;
//         }

//         @keyframes spin {
//           0% { transform: rotate(0deg); }
//           100% { transform: rotate(360deg); }
//         }

//         .custom-arrow {
//           position: absolute;
//           top: 50%;
//           transform: translateY(-50%);
//           z-index: 1;
//           background: rgba(0, 0, 0, 0.6);
//           width: 36px;
//           height: 36px;
//           display: flex;
//           align-items: center;
//           justify-content: center;
//           border-radius: 50%;
//           cursor: pointer;
//           color: #fff;
//           transition: all 0.2s ease;
//         }

//         .custom-arrow:hover {
//           background: rgba(0, 0, 0, 0.8);
//           transform: translateY(-50%) scale(1.1);
//         }

//         .custom-arrow.next {
//           right: 12px;
//         }

//         .custom-arrow.prev {
//           left: 12px;
//         }

//         .slick-dots {
//           bottom: 20px;
//           display: flex !important;
//           justify-content: center;
//           padding: 0;
//           margin: 0;
//           list-style: none;
//           z-index: 2;
//         }

//         .slick-dots li {
//           width: 12px;
//           height: 12px;
//           margin: 0 4px;
//         }

//         .slick-dots li button {
//           width: 12px;
//           height: 12px;
//           padding: 0;
//           border-radius: 50%;
//           background: rgba(255, 255, 255, 0.5);
//           border: 2px solid rgba(255, 255, 255, 0.8);
//           transition: all 0.3s ease;
//           cursor: pointer;
//         }

//         .slick-dots li button:hover {
//           background: rgba(255, 255, 255, 0.8);
//           transform: scale(1.1);
//         }

//         .slick-dots li.slick-active button {
//           background: #fff;
//           border-color: #fff;
//           transform: scale(1.3);
//           box-shadow: 0 0 8px rgba(255, 255, 255, 0.6);
//         }

//         .slick-dots li button:before {
//           content: none;
//         }

//         @media (max-width: 768px) {
//           .car-listing-card {
//             margin-bottom: 20px;
//           }

//           .car-header {
//             flex-direction: column;
//             gap: 8px;
//           }

//           .price-tag {
//             align-self: flex-start;
//             margin-left: 0;
//           }
//         }
//       `}</style>
//     </div>
//   );
// };

// export default CarListingBookedLayer;

